import { BetaLabel, Loading, TrendLabel } from '@/components/customComponents'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { trendChartColor } from '@/constants'
import { formatNumber } from '@/utils'
import ReactEcharts from 'echarts-for-react'
import React from 'react'

export type MetricCardChartPoint = {
  date: string | number
  metric: number
}

interface MetricCardProps {
  title: string
  subTitle?: string
  content: string | number | undefined
  type: 'money' | 'percentage' | 'number' | 'hour' | 'seconds'
  mtdCurrContent?: number | undefined
  ytdCurrContent?: number | undefined
  mtdPrevContent?: number | undefined
  ytdPrevContent?: number | undefined
  kpiGoal?: number | null
  loading?: boolean
  showToDate?: boolean
  quarterToDate?: boolean
  icon: React.ReactNode
  color: { bg: string; color: string }
  isIncreasingPositive?: boolean
  basicCard?: boolean
  isBeta?: boolean
  /** Optional chart data; chart is shown only when provided and non-empty */
  chart?:
    | MetricCardChartPoint[]
    | {
        trend: MetricCardChartPoint[]
        current: MetricCardChartPoint[]
      }
  // TODO: remove this after finalize
  isNewLogic?: boolean
}

const formatContent = (
  content: string | number | undefined,
  type: MetricCardProps['type']
) => {
  switch (type) {
    case 'hour':
      return (
        <span>
          {formatNumber(content, type, false)}
          <span className="text-sm">hr</span>
        </span>
      )
    case 'seconds':
      return (
        <span>
          {formatNumber(content, type, false)}
          <span className="text-sm">secs</span>
        </span>
      )
    case 'number':
    case 'money':
    case 'percentage':
      return formatNumber(content, type)
    default:
      return content
  }
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  mtdCurrContent,
  ytdCurrContent,
  mtdPrevContent,
  ytdPrevContent,
  subTitle,
  content,
  type,
  loading = false,
  showToDate = true,
  quarterToDate = false,
  kpiGoal,
  icon,
  color,
  isIncreasingPositive = true,
  basicCard = false,
  isBeta = false,
  chart: chartData,
  isNewLogic = false,
}) => {
  const showChart =
    !!chartData &&
    (Array.isArray(chartData)
      ? chartData.length > 0
      : !!(
          (chartData.trend && chartData.trend.length > 0) ||
          (chartData.current && chartData.current.length > 0)
        ))
  return (
    <Card>
      <CardContent className="p-6">
        <div
          className={
            basicCard ? 'flex flex-col space-y-1' : 'flex flex-col space-y-3'
          }
          style={{ position: 'relative' }}
        >
          {/* Header with title and icon */}
          <div className="flex justify-between items-start">
            <div
              className={basicCard ? 'h-12 flex flex-col justify-start' : ''}
            >
              <span className="text-text-l text-gray-500">{title}</span>
              <p className="text-text-m text-gray-500">
                {subTitle || '\u00A0'}
              </p>
            </div>
            {icon && color && (
              <div
                className={`w-8 h-8 min-w-8 rounded-full flex items-center justify-center flex-shrink-0 ${color.bg}`}
              >
                {React.isValidElement(icon)
                  ? React.cloneElement(icon as React.ReactElement<any>)
                  : icon}
              </div>
            )}
          </div>

          {/* Line Chart - only when chart data is provided and non-empty */}
          {!loading && !basicCard && showChart && (
            <div className="absolute right-2 top-[50%] -translate-y-1/2 w-3/5 h-20 pointer-events-none z-10">
              <ReactEcharts
                option={getMetricCardChartOptions(color.color, chartData)}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          )}

          {/* Main value */}
          <div
            className={`flex justify-between items-center ${basicCard ? 'mt-1' : ''}`}
          >
            {!loading && checkIfValidContent(content) ? (
              <p className="text-display-l text-black-500">
                {formatContent(content, type)}
              </p>
            ) : (
              !loading && <p className="text-4xl font-bold text-gray-900">-</p>
            )}
          </div>

          {loading ? (
            <Loading />
          ) : (
            <>
              {showToDate && !basicCard && (
                <div className="space-y-3">
                  {/* MTD and YTD trends */}
                  <div className="flex items-center space-x-6">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <div className="flex items-center space-x-2">
                            <span className="text-text-m text-gray-500">
                              {quarterToDate ? 'QoQ' : 'MoM'}
                            </span>
                            <TrendLabel
                              oldValue={mtdPrevContent || 0}
                              newValue={mtdCurrContent || 0}
                              isNormal={isIncreasingPositive}
                              trendType={type}
                              isMetricCard={true}
                              isNewLogic={isNewLogic}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <span>
                            {quarterToDate
                              ? 'Quarter over Quarter'
                              : 'Month over Month'}
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <div className="flex items-center space-x-2">
                            <span className="text-text-m text-gray-500">
                              YoY
                            </span>
                            <TrendLabel
                              oldValue={ytdPrevContent || 0}
                              newValue={ytdCurrContent || 0}
                              isNormal={isIncreasingPositive}
                              trendType={type}
                              isMetricCard={true}
                              isNewLogic={isNewLogic}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <span>Year over Year</span>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {kpiGoal && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="flex items-center space-x-2">
                              <span className="text-text-m text-gray-500">
                                KPI
                              </span>
                              <div className="text-text-m text-gray-500">
                                {kpiGoal ? formatContent(kpiGoal, type) : '-'}
                              </div>
                              {/* <TrendLabel
                              oldValue={ytdPrevContent || 0}
                              newValue={ytdCurrContent || 0}
                              isNormal={true}
                              trendType={type}
                              isMetricCard={true}
                            /> */}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span>KPI Goal</span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
      {isBeta && (
        <CardFooter className="flex justify-end items-end p-0">
          <BetaLabel />
        </CardFooter>
      )}
    </Card>
  )
}

function checkIfValidContent(content: string | number | undefined) {
  if (content === 0) return true

  return (
    content && content !== undefined && content !== null && content !== 'NaN'
  )
}

type SegmentedMetricCardChartPoint = MetricCardChartPoint & {
  segment: 'trend' | 'current'
}

function mergeTrendAndCurrentChartData(
  trend: MetricCardChartPoint[],
  current: MetricCardChartPoint[]
): SegmentedMetricCardChartPoint[] {
  // FIXME: find a way to connect the trend and current points to each other
  const trendMap = trend.map((point, index) => [index as number, point.metric])
  const currentMap = current.map((point, index) => [
    (index + trend.length) as number,
    point.metric,
  ])

  const merged: SegmentedMetricCardChartPoint[] = [
    ...trendMap.map((point) => ({
      date: point[0],
      metric: point[1],
      segment: 'trend' as const,
    })),
    ...currentMap.map((point) => ({
      date: point[0],
      metric: point[1],
      segment: 'current' as const,
    })),
  ]

  return merged
}

function getMetricCardChartOptions(
  color: string,
  chartData:
    | MetricCardChartPoint[]
    | {
        trend: MetricCardChartPoint[]
        current: MetricCardChartPoint[]
      }
    | undefined
): import('echarts').EChartsOption {
  if (!chartData) {
    return getLineChartOptions(color, [])
  }

  if (Array.isArray(chartData)) {
    return getLineChartOptions(color, chartData)
  }

  const trend = chartData.trend || []
  const current = chartData.current || []

  if (trend.length > 0 && current.length > 0) {
    return getSegmentedLineChartOptions(color, trend, current)
  }

  const combined = [...trend, ...current]
  return getLineChartOptions(color, combined)
}

function getSegmentedLineChartOptions(
  color: string,
  trend: MetricCardChartPoint[],
  current: MetricCardChartPoint[]
): import('echarts').EChartsOption {
  const merged = mergeTrendAndCurrentChartData(trend, current)

  const categories = merged.map((point) => point.date)

  const trendSeriesData = merged.map((point) =>
    point.segment === 'trend' ? point.metric : null
  )

  const currentSeriesData = merged.map((point) =>
    point.segment === 'current' ? point.metric : null
  )

  return {
    grid: {
      bottom: '10%',
      left: '0%',
      right: '0%',
      top: '20%',
    },
    xAxis: {
      type: 'category',
      show: false,
      data: categories,
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    series: [
      {
        name: 'Trend',
        type: 'line',
        data: trendSeriesData,
        symbol: 'none',
        smooth: true,
        connectNulls: true,
        lineStyle: {
          color: trendChartColor,
          width: 2,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: trendChartColor + '40',
              },
              {
                offset: 1,
                color: trendChartColor + '00',
              },
            ],
          },
        },
      },
      {
        name: 'Current',
        type: 'line',
        data: currentSeriesData,
        symbol: 'none',
        connectNulls: true,
        smooth: true,
        lineStyle: {
          color: color,
          width: 2,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: color + '40',
              },
              {
                offset: 1,
                color: color + '00',
              },
            ],
          },
        },
      },
    ],
  }
}

function getLineChartOptions(
  color: string,
  data: MetricCardChartPoint[]
): import('echarts').EChartsOption {
  return {
    grid: {
      bottom: '10%',
      left: '0%',
      right: '0%',
      top: '20%',
    },
    xAxis: {
      type: 'category',
      show: false,
      data: data.map((d) => d.date),
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    series: [
      {
        name: 'Metric',
        type: 'line',
        data: data.map((d) => d.metric),
        itemStyle: {
          color: color,
        },
        lineStyle: {
          color: color,
          width: 2,
        },
        symbol: 'none',
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: color + '40',
              },
              {
                offset: 1,
                color: color + '00',
              },
            ],
          },
        },
      },
    ],
  }
}

export { MetricCard }
